/* eslint-disable react-refresh/only-export-components */

import React, { useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U3129TaskItem, U3129Timesheet, U3129InitLU, U3129InitLUEmployee } from "./U3129Service";
import type { U3129PeriodPreset } from "./tools/U3129PeriodPresets";
import type { U2101InitLU } from "../u2101LogicalUnit/U2101";
import { U2000_ORG_DISPLAY_TIMEZONE, u2000SetActiveDisplayTimezone } from "../u2000Core/tools/U2000DateTimeHelpers";

export interface U3129ContextType {
  initLU: U3129InitLU;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3129Timesheet>;
  /** Employé dont on affiche les feuilles de temps (obligatoire pour la recherche). */
  employeeId: string | null;
  setEmployeeId: (id: string | null) => void;
  /** Objet employé de l'initLU (persisté pour l'assigner à la création d'une feuille de temps). */
  employee: U3129InitLUEmployee | null;
  /** Si true, la recherche U3129 inclut l'ensemble des employés (rôle U3129:OTHEREMPLOYEE). */
  showAllEmployees: boolean;
  setShowAllEmployees: (value: boolean) => void;
  /** Préréglage période conservé à l'échelle de l'UL (liste/formulaire). */
  periodPreset: U3129PeriodPreset;
  setPeriodPreset: (value: U3129PeriodPreset) => void;
  /** Empêche de réappliquer le preset par défaut au retour formulaire -> liste. */
  periodPresetInitialized: boolean;
  setPeriodPresetInitialized: (value: boolean) => void;
  /** Tâches chargées par ligne honoraire (`clientRowId`), pour le payload de sauvegarde. */
  honorariumTasksByClientRowId: Record<string, U3129TaskItem[]>;
  setHonorariumTasksForRow: (clientRowId: string, tasks: U3129TaskItem[]) => void;
  /** Onglet actif du formulaire feuille (Honoraires / Dépenses / Équipement) — mémoire session, pas localStorage. */
  formActiveTab: string;
  setFormActiveTab: (tab: string) => void;
}

interface U3129ContextProviderProps {
  initLU: U3129InitLU;
  children: ReactNode;
}

const U3129Context = React.createContext<U3129ContextType | undefined>(undefined);

export const U3129ContextProvider: React.FC<U3129ContextProviderProps> = ({ initLU, children }) => {
  u2000SetActiveDisplayTimezone(initLU.organizationTimezone ?? U2000_ORG_DISPLAY_TIMEZONE);

  const luStateHandlers = useU2000LUStateHandlers({
    initLU: initLU as unknown as U2101InitLU,
  });
  const searchStateHandlers = useU2000SearchStateHandlers({
    luName: "u3129",
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const gridStateHandlers = useU2000GridStateHandlers<U3129Timesheet>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });
  const [employeeId, setEmployeeId] = useState<string | null>(initLU.employee?.id ?? null);
  const [showAllEmployees, setShowAllEmployees] = useState<boolean>(false);
  const [periodPreset, setPeriodPreset] = useState<U3129PeriodPreset>(initLU.defaultPeriodPreset ?? "current_week");
  const [periodPresetInitialized, setPeriodPresetInitialized] = useState<boolean>(false);
  const [honorariumTasksByClientRowId, setHonorariumTasksByClientRowId] = useState<Record<string, U3129TaskItem[]>>({});
  const [formActiveTab, setFormActiveTab] = useState<string>("tabHonoraria");
  const employee = initLU.employee ?? null;

  const setHonorariumTasksForRow = useCallback((clientRowId: string, tasks: U3129TaskItem[]) => {
    setHonorariumTasksByClientRowId((prev) => ({ ...prev, [clientRowId]: tasks }));
  }, []);

  const contextValue = useMemo<U3129ContextType>(
    () => ({
      initLU,
      luStateHandlers,
      searchStateHandlers,
      gridStateHandlers,
      employeeId,
      setEmployeeId,
      employee,
      showAllEmployees,
      setShowAllEmployees,
      periodPreset,
      setPeriodPreset,
      periodPresetInitialized,
      setPeriodPresetInitialized,
      honorariumTasksByClientRowId,
      setHonorariumTasksForRow,
      formActiveTab,
      setFormActiveTab,
    }),
    [
      initLU,
      luStateHandlers,
      searchStateHandlers,
      gridStateHandlers,
      employeeId,
      employee,
      showAllEmployees,
      periodPreset,
      periodPresetInitialized,
      honorariumTasksByClientRowId,
      setHonorariumTasksForRow,
      formActiveTab,
    ]
  );

  return <U3129Context.Provider value={contextValue}>{children}</U3129Context.Provider>;
};

export function useU3129Context(): U3129ContextType {
  const context = useContext(U3129Context);
  if (!context) {
    throw new Error("useU3129Context must be used within a U3129ContextProvider");
  }
  return context;
}
